import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, IconButton, Snackbar, TextField, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { gamesApi } from '../../../services/api/gamesApi';
import { authApi } from '../../../services/api/authApi';
import './PlinkoScreen.css';

const RISKS = [
  { key: 'LOW', label: 'Low' },
  { key: 'MEDIUM', label: 'Med' },
  { key: 'HIGH', label: 'High' },
];
const BET_CHIPS = [10, 50, 100, 500];
const STEP_MS = 280;
const FINAL_PAUSE_MS = 450;

function delay(ms) {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

function ballLeftAtStep(rights, stepIndex, rowCount) {
  return 50 + (2 * rights - (stepIndex + 1)) * (50 / rowCount);
}

function ballLeftAtSlot(slotIndex, rowCount) {
  return 50 + (2 * slotIndex - rowCount) * (50 / rowCount);
}

function PlinkoScreen({ user, onBack, onWalletUpdate }) {
  const [state, setState] = useState(null);
  const [amount, setAmount] = useState('50');
  const [risk, setRisk] = useState('MEDIUM');
  const [rows, setRows] = useState(8);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [lastDrop, setLastDrop] = useState(null);
  const [animSlot, setAnimSlot] = useState(null);
  const [ball, setBall] = useState(null);
  const animRunRef = useRef(0);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
    } catch { /* ignore */ }
  }, [onWalletUpdate]);

  const load = useCallback(() => {
    gamesApi.plinkoState().then(setState).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const multipliers = state?.multipliers?.[risk]?.[rows] ?? [];
  const controlsLocked = loading || isAnimating;

  const animateBallDrop = useCallback(async (path, slotIndex, rowCount) => {
    const runId = animRunRef.current + 1;
    animRunRef.current = runId;
    setIsAnimating(true);
    setLastDrop(null);
    setAnimSlot(null);
    setBall({ left: 50, top: 2 });

    await delay(STEP_MS);
    if (animRunRef.current !== runId) return;

    let rights = 0;
    const steps = Array.isArray(path) ? path : [];

    for (let i = 0; i < steps.length; i += 1) {
      rights += steps[i] ? 1 : 0;
      setBall({
        left: ballLeftAtStep(rights, i, rowCount),
        top: 8 + ((i + 1) / rowCount) * 72,
      });
      await delay(STEP_MS);
      if (animRunRef.current !== runId) return;
    }

    setBall({
      left: ballLeftAtSlot(slotIndex, rowCount),
      top: 88,
    });
    setAnimSlot(slotIndex);
    await delay(FINAL_PAUSE_MS);
    if (animRunRef.current !== runId) return;

    setIsAnimating(false);
  }, []);

  useEffect(() => () => { animRunRef.current += 1; }, []);

  const drop = async () => {
    const stake = Number(amount);
    if (!Number.isFinite(stake) || stake <= 0 || stake > walletBalance) {
      setToast('Invalid amount or insufficient balance');
      return;
    }
    setLoading(true);
    setBall(null);
    try {
      const result = await gamesApi.plinkoDrop({ amount: stake, risk, rows });
      await animateBallDrop(result.bet.path, result.bet.slotIndex, rows);
      setLastDrop(result.bet);
      setToast(
        result.bet.payout >= stake
          ? `Won ₹${result.bet.payout} (${result.bet.multiplier}x)!`
          : `Landed ${result.bet.multiplier}x — ₹${result.bet.payout}`,
      );
      await refreshUser();
      load();
    } catch (err) {
      setBall(null);
      setIsAnimating(false);
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="plinko-root">
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />

      <Box className="plinko-header">
        <IconButton size="small" onClick={onBack} className="plinko-back">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="plinko-title">Plinko</Typography>
        <Chip size="small" label={`₹${walletBalance.toLocaleString('en-IN')}`} className="plinko-wallet" />
      </Box>

      <Box className="plinko-scroll">
        <Box className="plinko-board">
          <Box className="plinko-pegs" style={{ '--rows': rows }}>
            {Array.from({ length: rows }).map((_, r) => (
              <Box key={r} className="plinko-peg-row" style={{ '--cols': r + 2 }}>
                {Array.from({ length: r + 2 }).map((__, c) => (
                  <span key={c} className="plinko-peg" />
                ))}
              </Box>
            ))}
          </Box>
          {ball && (
            <span
              className={`plinko-ball ${isAnimating ? 'plinko-ball--falling' : 'plinko-ball--landed'}`}
              style={{ left: `${ball.left}%`, top: `${ball.top}%` }}
            />
          )}
          <Box className="plinko-slots">
            {multipliers.map((m, i) => (
              <Box
                key={i}
                className={`plinko-slot ${animSlot === i ? 'plinko-slot--hit' : ''} ${m >= 2 ? 'hot' : m < 1 ? 'cold' : ''}`}
              >
                {m}x
              </Box>
            ))}
          </Box>
        </Box>

        {lastDrop && (
          <Box className={`plinko-result plinko-result--${lastDrop.profit >= 0 ? 'win' : 'loss'}`}>
            <Typography>{lastDrop.multiplier}x → ₹{lastDrop.payout}</Typography>
            <Typography className="plinko-result__sub">
              {lastDrop.profit >= 0 ? `+₹${lastDrop.profit}` : `₹${lastDrop.profit}`}
            </Typography>
          </Box>
        )}

        <Box className="plinko-controls">
          <Typography className="plinko-label">Risk</Typography>
          <Box className="plinko-risk-row">
            {RISKS.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`plinko-risk-btn ${risk === r.key ? 'active' : ''}`}
                disabled={controlsLocked}
                onClick={() => setRisk(r.key)}
              >
                {r.label}
              </button>
            ))}
          </Box>

          <Typography className="plinko-label">Rows</Typography>
          <Box className="plinko-risk-row">
            {(state?.allowedRows ?? [8, 12]).map((r) => (
              <button
                key={r}
                type="button"
                className={`plinko-risk-btn ${rows === r ? 'active' : ''}`}
                disabled={controlsLocked}
                onClick={() => setRows(r)}
              >
                {r}
              </button>
            ))}
          </Box>

          <Typography className="plinko-label">Bet (₹)</Typography>
          <TextField fullWidth size="small" value={amount} onChange={(e) => setAmount(e.target.value)} className="plinko-input" />
          <Box className="plinko-chips">
            {BET_CHIPS.map((c) => (
              <button key={c} type="button" className={`plinko-chip ${Number(amount) === c ? 'active' : ''}`} onClick={() => setAmount(String(c))}>
                ₹{c}
              </button>
            ))}
          </Box>

          <Button fullWidth className="plinko-drop-btn" onClick={drop} disabled={controlsLocked || !state?.enabled}>
            {isAnimating ? 'Dropping…' : `Drop ball · ₹${amount || 0}`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default PlinkoScreen;
